import { useCallback, useRef } from 'react';

// Capturing a spoken answer, and knowing when it ended.
//
// It records through the Web Audio API into a 16-bit PCM WAV rather than
// through MediaRecorder, which produces webm or mp4. The transcriber accepts
// wav, mp3, ogg and flac and refuses webm, so capturing WAV directly avoids a
// transcode on the server and behaves the same in Chrome, Firefox and Safari.
//
// It stops itself. A child does not hold a button down while they think; they
// answer and then they are quiet, so the recorder watches for trailing silence
// and ends the turn on it.

export interface Recording {
  blob: Blob;
  mimeType: string;
}

function audioContextCtor(): typeof AudioContext | undefined {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

export function recorderSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    audioContextCtor() !== undefined
  );
}

interface RecordOptions {
  /** Trailing quiet, in ms, that ends the turn. */
  silenceMs?: number;
  /** A hard cap on one turn. */
  maxMs?: number;
  /** If nothing is said within this window, give up and return null. */
  noSpeechMs?: number;
}

export function useRecorder() {
  const stopRef = useRef<(() => void) | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const record = useCallback((opts: RecordOptions = {}): Promise<Recording | null> => {
    const { silenceMs = 1300, maxMs = 12000, noSpeechMs = 7000 } = opts;

    return new Promise<Recording | null>((resolve, reject) => {
      const AC = audioContextCtor();
      if (!recorderSupported() || !AC) {
        resolve(null);
        return;
      }

      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          const ctx = new AC();
          if (ctx.state === 'suspended') void ctx.resume().catch(() => {});

          const source = ctx.createMediaStreamSource(stream);
          const processor = ctx.createScriptProcessor(4096, 1, 1);
          // Through a muted gain: some browsers only fire onaudioprocess for a
          // node connected to the destination, and nobody wants to hear
          // themselves echoed.
          const mute = ctx.createGain();
          mute.gain.value = 0;

          const chunks: Float32Array[] = [];
          let total = 0;
          let stopped = false;
          let canceled = false;
          let speechStarted = false;
          let lastLoudAt = 0;
          const startedAt = performance.now();

          const cleanup = () => {
            processor.onaudioprocess = null;
            try {
              processor.disconnect();
              source.disconnect();
              mute.disconnect();
            } catch {
              // already torn down
            }
            stream.getTracks().forEach((t) => t.stop());
            if (ctx.state !== 'closed') void ctx.close().catch(() => {});
            stopRef.current = null;
            cancelRef.current = null;
          };

          const finish = () => {
            if (stopped) return;
            stopped = true;
            cleanup();
            if (canceled || total === 0) {
              resolve(null);
              return;
            }
            resolve({ blob: encodeWAV(merge(chunks, total), ctx.sampleRate), mimeType: 'audio/wav' });
          };

          processor.onaudioprocess = (e) => {
            if (stopped) return;
            const input = e.inputBuffer.getChannelData(0);
            const copy = new Float32Array(input.length);
            copy.set(input);
            chunks.push(copy);
            total += copy.length;

            let sum = 0;
            for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
            const rms = Math.sqrt(sum / input.length);
            const now = performance.now();
            if (rms > 0.02) {
              speechStarted = true;
              lastLoudAt = now;
            }
            const elapsed = now - startedAt;
            if (elapsed > maxMs) return finish();
            if (!speechStarted && elapsed > noSpeechMs) return finish();
            if (speechStarted && now - lastLoudAt > silenceMs) return finish();
          };

          source.connect(processor);
          processor.connect(mute);
          mute.connect(ctx.destination);

          stopRef.current = finish;
          cancelRef.current = () => {
            canceled = true;
            finish();
          };
        })
        .catch((err: unknown) => reject(err instanceof Error ? err : new Error('mic error')));
    });
  }, []);

  /** Ends the turn early but keeps what was captured. */
  const stop = useCallback(() => stopRef.current?.(), []);
  /** Throws the turn away. */
  const cancel = useCallback(() => cancelRef.current?.(), []);

  return { record, stop, cancel };
}

function merge(chunks: Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

// encodeWAV writes mono 16-bit PCM into a canonical WAV. The transcriber takes
// any sample rate, so this keeps the context's native one rather than
// resampling in JavaScript.
function encodeWAV(samples: Float32Array, sampleRate: number): Blob {
  const dataLen = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buffer);
  const str = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  str(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  str(36, 'data');
  view.setUint32(40, dataLen, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}
